/* ============================================================
   storage — markers on a video.

   Anyone who can open a video can drop a marker at any moment in
   it. Everyone who can open it sees every marker, who placed it
   and when, and can add notes under any marker — a small thread
   pinned to one second of the file. Your own markers and notes
   are yours to move, edit or remove; an admin can remove anything.

   Two surfaces: pins on the player's seek rail, coloured by who
   placed them, and a panel listing every marker with its notes.
   The list is refreshed while the player is open, so someone
   else's marker turns up without a reload.
   ============================================================ */

import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from './api';
import { fmtDate } from './lib';

const POLL_MS = 30000;
const NOTE_MAX = 2000;

/* One colour per person, the same on every screen: hashed from the user id
   into the data-series palette. chart-1 is skipped — it is the accent the
   playhead already uses, and a marker must not look like the playhead. */
const PALETTE = ['--chart-2', '--chart-3', '--chart-4', '--chart-5', '--chart-6', '--chart-7', '--chart-8'];
export function markerColor(userId) {
  let h = 0;
  for (const ch of String(userId || '')) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return `var(${PALETTE[h % PALETTE.length]})`;
}

/** The marker list for one file, and every change to it. */
export function useMarkers({ token, fileId, enabled = true }) {
  const [markers, setMarkers] = useState([]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const live = useRef(true);

  const load = useCallback(async (signal) => {
    if (!token || !fileId || !enabled) return;
    try {
      const d = await api.markers(token, fileId, signal);
      if (live.current) { setMarkers(d.markers || []); setError(''); }
    } catch (e) {
      if (e.name !== 'AbortError' && live.current) setError(e.message);
    }
  }, [token, fileId, enabled]);

  useEffect(() => {
    live.current = true;
    setMarkers([]); setError('');
    const ctrl = new AbortController();
    load(ctrl.signal);
    // Someone else's marker should appear while you watch, without a reload —
    // but only while the tab is actually being looked at.
    const id = window.setInterval(() => { if (!document.hidden) load(); }, POLL_MS);
    const onVis = () => { if (!document.hidden) load(); };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      live.current = false;
      ctrl.abort();
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [load]);

  /* Every mutation answers with the whole list: replace, never patch. */
  const run = useCallback(async (fn) => {
    setBusy(true);
    try {
      const d = await fn();
      if (live.current) { setMarkers(d.markers || []); setError(''); }
      return d;
    } catch (e) {
      if (live.current) setError(e.message);
      return null;
    } finally {
      if (live.current) setBusy(false);
    }
  }, []);

  // Stable across renders, so the player's key handler can hold on to them.
  const actions = useMemo(() => ({
    add: (at, note) => run(() => api.addMarker(token, fileId, at, note)),
    move: (id, at) => run(() => api.moveMarker(token, id, at)),
    remove: (id) => run(() => api.deleteMarker(token, id)),
    addNote: (id, body) => run(() => api.addMarkerNote(token, id, body)),
    editNote: (noteId, body) => run(() => api.editMarkerNote(token, noteId, body)),
    removeNote: (noteId) => run(() => api.deleteMarkerNote(token, noteId)),
  }), [token, fileId, run]);

  return useMemo(() => ({ markers, error, busy, reload: load, ...actions }),
    [markers, error, busy, load, actions]);
}

/* ── pins on the seek rail ────────────────────────────────────────────── */

/* A layer laid over the seek rail rather than inside it: the rail is a
   slider, and a slider has no business containing buttons. Being beside it
   also means a pin never starts a scrub. */
export const MarkerPins = memo(function MarkerPins({ markers, duration, focusId, onPick, fmtTime }) {
  const [hover, setHover] = useState(null);
  if (!Number.isFinite(duration) || duration <= 0 || !markers.length) return null;
  const tip = hover && markers.find((m) => m.id === hover);
  const at = (t) => `${Math.min(100, Math.max(0, (t / duration) * 100))}%`;
  return (
    <div className="st-mk-pins">
      {markers.map((m) => {
        const first = m.notes[0]?.body;
        return (
          <button
            key={m.id}
            type="button"
            className={`st-mk-pin${m.id === focusId ? ' is-focus' : ''}`}
            style={{ left: at(m.at), '--mk': markerColor(m.user_id) }}
            aria-label={`Marker at ${fmtTime(m.at)} by ${m.username}${first ? `: ${first}` : ''}`}
            onClick={() => onPick(m)}
            onPointerEnter={() => setHover(m.id)}
            onPointerLeave={() => setHover((h) => (h === m.id ? null : h))}
            onFocus={() => setHover(m.id)}
            onBlur={() => setHover((h) => (h === m.id ? null : h))}
          />
        );
      })}
      {tip && (
        <div className="st-mk-tip" style={{ '--x': at(tip.at) }}>
          <span className="st-mk-tip-head">
            <span className="tnum">{fmtTime(tip.at)}</span>
            <span className="st-mk-dot" style={{ '--mk': markerColor(tip.user_id) }}/>
            <span className="st-mk-tip-who">{tip.mine ? 'You' : tip.username}</span>
            {tip.notes.length > 1 && <span className="st-mk-tip-n">{tip.notes.length} notes</span>}
          </span>
          {tip.notes[0] && <span className="st-mk-tip-body">{tip.notes[0].body}</span>}
        </div>
      )}
    </div>
  );
});

/* ── the panel ────────────────────────────────────────────────────────── */

/* A note box: Enter adds, Shift+Enter is a new line, Escape hands the keys
   back to the player. Grows with its text up to a few lines. */
function NoteBox({ initial = '', placeholder, submitLabel, onSubmit, onCancel, autoFocus, busy, onEscape }) {
  const [text, setText] = useState(initial);
  const ref = useRef(null);
  useEffect(() => { if (autoFocus) ref.current?.focus(); }, [autoFocus]);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 110)}px`;
  }, [text]);
  const trimmed = text.trim();
  const submit = async () => {
    if (!trimmed || busy) return;
    const ok = await onSubmit(trimmed);
    if (ok) setText('');
  };
  return (
    <form className="st-mk-form" onSubmit={(e) => { e.preventDefault(); submit(); }}>
      <textarea
        ref={ref}
        className="st-mk-input"
        rows={1}
        value={text}
        maxLength={NOTE_MAX}
        placeholder={placeholder}
        aria-label={placeholder}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(); }
          else if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); if (onCancel) onCancel(); else onEscape?.(); }
        }}
      />
      <div className="st-mk-formacts">
        {onCancel && <button type="button" className="st-vp-link st-vp-link--dim" onClick={onCancel}>Cancel</button>}
        <button type="submit" className="st-mk-submit" disabled={!trimmed || busy}>{submitLabel}</button>
      </div>
    </form>
  );
}

/* `grouped`: the same person as the line above — the marker's own author
   for its first note — so the name is not said twice in a row. */
function Note({ note, marks, onEscape, grouped }) {
  const [editing, setEditing] = useState(false);
  const [confirm, setConfirm] = useState(false);
  if (editing) {
    return (
      <li className="st-mk-note is-editing">
        <NoteBox
          initial={note.body} placeholder="Edit note" submitLabel="Save" autoFocus busy={marks.busy}
          onSubmit={async (body) => { const d = await marks.editNote(note.id, body); if (d) setEditing(false); return !!d; }}
          onCancel={() => setEditing(false)}
          onEscape={onEscape}
        />
      </li>
    );
  }
  const when = new Date(note.created_at * 1000).toLocaleString();
  const acts = (note.can_edit || note.can_delete) && (
    confirm ? (
      <span className="st-mk-acts">
        <button type="button" className="st-vp-link st-mk-danger" onClick={() => marks.removeNote(note.id)}>Delete note</button>
        <button type="button" className="st-vp-link st-vp-link--dim" onClick={() => setConfirm(false)}>Keep</button>
      </span>
    ) : (
      <span className="st-mk-acts">
        {note.can_edit && <button type="button" className="st-vp-link st-vp-link--dim" onClick={() => setEditing(true)}>Edit</button>}
        {note.can_delete && <button type="button" className="st-vp-link st-vp-link--dim" onClick={() => setConfirm(true)}>Delete</button>}
      </span>
    )
  );
  // A follow-on note is just its text, with its links at the end of it: a
  // line holding nothing but a time would be most of what the list shows.
  if (grouped) {
    return (
      <li className="st-mk-note is-grouped">
        <p className="st-mk-body" title={when}>
          {note.body}
          {note.updated_at ? <span className="st-mk-when"> · edited</span> : null}
          {acts}
        </p>
      </li>
    );
  }
  return (
    <li className="st-mk-note">
      <div className="st-mk-note-head">
        <span className="st-mk-dot" style={{ '--mk': markerColor(note.user_id) }}/>
        <b>{note.mine ? 'You' : note.username}</b>
        <span className="st-mk-when" title={when}>
          {fmtDate(note.created_at)}{note.updated_at ? ' · edited' : ''}
        </span>
        {acts}
      </div>
      <p className="st-mk-body">{note.body}</p>
    </li>
  );
}

function Item({ m, marks, focused, here, current, getNow, fmtTime, onSeek, onFocus, focusNote, onEscape }) {
  const ref = useRef(null);
  const [confirm, setConfirm] = useState(false);
  useEffect(() => {
    if (focused) ref.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [focused]);
  const moved = Math.abs(current - m.at) >= 0.5;
  return (
    <li ref={ref} className={`st-mk-item${focused ? ' is-focus' : ''}${here ? ' is-here' : ''}`}
        style={{ '--mk': markerColor(m.user_id) }}
        onClick={() => onFocus(m.id)}>
      <div className="st-mk-head">
        <button type="button" className="st-mk-time tnum" onClick={(e) => { e.stopPropagation(); onSeek(m); }}
                title={`Jump to ${fmtTime(m.at)}`}>
          {fmtTime(m.at)}
        </button>
        <span className="st-mk-dot"/>
        <span className="st-mk-who">{m.mine ? 'You' : m.username}</span>
        <span className="st-mk-when" title={new Date(m.created_at * 1000).toLocaleString()}>{fmtDate(m.created_at)}</span>
        {!confirm && (m.can_move || m.can_delete) && (
          <span className="st-mk-acts">
            {m.can_move && moved && (
              <button type="button" className="st-vp-link st-vp-link--dim"
                      onClick={(e) => { e.stopPropagation(); marks.move(m.id, getNow()); }}
                      title="Move this marker to where the video is now">
                Move to {fmtTime(current)}
              </button>
            )}
            {m.can_delete && (
              <button type="button" className="st-vp-link st-vp-link--dim"
                      onClick={(e) => { e.stopPropagation(); setConfirm(true); }}>Delete</button>
            )}
          </span>
        )}
      </div>
      {confirm && (
        <div className="st-mk-confirm" onClick={(e) => e.stopPropagation()}>
          <span>
            Delete this marker{m.notes.length ? ` and its ${m.notes.length === 1 ? 'note' : `${m.notes.length} notes`}` : ''}?
          </span>
          <button type="button" className="st-vp-link st-mk-danger" onClick={() => marks.remove(m.id)}>Delete</button>
          <button type="button" className="st-vp-link st-vp-link--dim" onClick={() => setConfirm(false)}>Keep</button>
        </div>
      )}
      {m.notes.length > 0 && (
        <ul className="st-mk-notes">
          {m.notes.map((n, i) => (
            <Note key={n.id} note={n} marks={marks} onEscape={onEscape}
                  grouped={n.user_id === (i ? m.notes[i - 1].user_id : m.user_id)}/>
          ))}
        </ul>
      )}
      {focused && (
        <div onClick={(e) => e.stopPropagation()}>
          <NoteBox
            placeholder={m.notes.length ? 'Add a note' : 'Add a note to this marker'}
            submitLabel="Add" busy={marks.busy} autoFocus={focusNote}
            onSubmit={async (body) => !!(await marks.addNote(m.id, body))}
            onEscape={onEscape}
          />
        </div>
      )}
    </li>
  );
}

/* `current` arrives rounded to the half second, so a playing video redraws
   this list twice a second rather than every frame; `getNow` is the exact
   time, for the one thing that needs it — moving a marker. */
export const MarkerPanel = memo(function MarkerPanel({
  marks, current, getNow, fmtTime, focusId, focusNote, onFocus, onSeek, onAdd, onClose, onEscape,
}) {
  const { markers, error } = marks;
  // "Here" is the marker the playhead is sitting on, so watching past one
  // lights it in the list.
  const hereId = markers.reduce((best, m) => (Math.abs(current - m.at) < 1.5 ? m.id : best), null);
  const people = new Set(markers.map((m) => m.user_id)).size;
  return (
    <aside className="st-mk-panel" aria-label="Markers" onClick={(e) => e.stopPropagation()}
           onDoubleClick={(e) => e.stopPropagation()}>
      <header className="st-mk-top">
        <h4>Markers <span className="st-mk-count tnum">{markers.length}</span></h4>
        {markers.length > 0 && people > 1 && <span className="st-mk-people">{people} people</span>}
        <button type="button" className="st-mk-addbtn" onClick={onAdd} disabled={marks.busy}
                title="Add a marker where the video is now (shift+M)">
          + Mark {fmtTime(current)}
        </button>
        <button type="button" className="st-vp-statsx st-mk-x" onClick={onClose} aria-label="Close markers">
          <svg width="12" height="12" viewBox="0 -960 960 960" fill="currentColor" aria-hidden="true">
            <path d="m249-207-42-42 231-231-231-231 42-42 231 231 231-231 42 42-231 231 231 231-42 42-231-231-231 231Z"/>
          </svg>
        </button>
      </header>
      {error && <p className="st-mk-error" role="alert">{error}</p>}
      {markers.length === 0 ? (
        <p className="st-mk-empty">
          No markers yet. Press <kbd>shift</kbd>+<kbd>M</kbd> or <b>+ Mark</b> to pin this moment. Everyone who can
          open this file will see it, and anyone can add notes to it.
        </p>
      ) : (
        <ol className="st-mk-list">
          {markers.map((m) => (
            <Item key={m.id} m={m} marks={marks} current={current} getNow={getNow} fmtTime={fmtTime}
                  focused={m.id === focusId} focusNote={m.id === focusId && focusNote}
                  here={m.id === hereId} onSeek={onSeek} onFocus={onFocus} onEscape={onEscape}/>
          ))}
        </ol>
      )}
    </aside>
  );
});
